import React from 'react';
import { PAYPAL_CLIENT_ID } from '@reverbdotcom/env';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import {
  CoreCheckoutStartPaypalCheckout,
  CoreCheckoutFinalizePaypalCheckoutPayment,
  CoreCheckoutPayPalPaymentInProgress,
  core_apimessages_Error as Error,
  CoreCheckoutUpdatePaypalShippingEstimate,
  core_apimessages_CheckoutLineItem_Type as CheckoutLineItemType,
} from '../schemas/rql';
import { MutationFn, ChildProps } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { useMutation } from '../lib/use_mutation_hook';
import { connect } from '@reverbdotcom/commons/src/connect';
import { buildPayPalStartPolling } from './buildPayPalStartPolling';
import { CheckoutActionHookFragment } from './checkoutActionHooks';
import Location from '../lib/wrapped_location';
import {
  OnShippingChangeData,
  OnShippingChangeActions,
} from '../lib/paypalVendorTypes';

type StartPaypalCheckoutMutationFn = MutationFn<
  CoreCheckoutStartPaypalCheckout.Mutation,
  CoreCheckoutStartPaypalCheckout.Variables
>;

type FinalizePaypalCheckoutPaymentMutationFn = MutationFn<
  CoreCheckoutFinalizePaypalCheckoutPayment.Mutation,
  CoreCheckoutFinalizePaypalCheckoutPayment.Variables
>;

type UpdatePaypalShippingEstimateMutationFn = MutationFn<
  CoreCheckoutUpdatePaypalShippingEstimate.Mutation,
  CoreCheckoutUpdatePaypalShippingEstimate.Variables
>;

interface MutationProps {
  startPaypalCheckout: StartPaypalCheckoutMutationFn;
  finalizePaypalCheckoutPayment: FinalizePaypalCheckoutPaymentMutationFn;
  updatePaypalShippingEstimate: UpdatePaypalShippingEstimateMutationFn;
}

interface ExternalProps {
  currency: string;
  checkoutBundlingId: string;
  setErrors: (errors: Error[]) => void;
}

type Props = ChildProps<
  ExternalProps & MutationProps,
  CoreCheckoutPayPalPaymentInProgress.Query,
  CoreCheckoutPayPalPaymentInProgress.Variables
>;

export function CheckoutPayPalButton(props: Props) {
  const { currency, checkoutBundlingId, setErrors } = props;
  const [startPaypalCheckout, { errors: startMutationErrors }] = useMutation(props.startPaypalCheckout);
  const [finalizePaypalCheckoutPayment, { errors: finalizeMutationErrors }] = useMutation(props.finalizePaypalCheckoutPayment);
  const [updateCheckoutShippingEstimate, { errors: updateShippingMutationErrors }] = useMutation(props.updatePaypalShippingEstimate);
  const checkoutRef = React.useRef('');

  const startPolling = buildPayPalStartPolling({
    data: props.data,
  });

  async function createOrder() {
    const result = await startPaypalCheckout({
      variables: {
        input: {
          checkoutBundlingId,
        },
      },
    });

    if (!result || !result.data) {
      setErrors(startMutationErrors);
    }

    if (result?.data?.startPaypalCheckout.paypalOrderId) {
      setErrors([]);
      checkoutRef.current = result.data.startPaypalCheckout.checkout?.id;
    }

    return result?.data?.startPaypalCheckout.paypalOrderId;
  }

  async function onApprove() {
    const result = await finalizePaypalCheckoutPayment({
      variables: {
        input: {
          id: checkoutRef.current,
        },
      },
    });

    if (!result || !result.data) {
      setErrors(finalizeMutationErrors);
    }

    if (result.data.finalizePaypalCheckoutPayment?.checkout.id) {
      setErrors([]);
    }

    const redirectUrl = await startPolling(checkoutRef.current);

    Location.replace(redirectUrl);
  }

  async function onShippingChange(data: OnShippingChangeData, actions: OnShippingChangeActions) {
    const { city, postal_code, state, country_code } = data.shipping_address;

    const result = await updateCheckoutShippingEstimate({
      variables: {
        input: {
          id: checkoutRef.current,
          shippingAddress: {
            locality: city,
            postalCode: postal_code,
            region: state,
            countryCode: country_code,
          },
        },
      },
    });

    if (!result || !result.data) {
      setErrors(updateShippingMutationErrors);
    }

    return actions.order.patch([
      {
        op: 'replace',
        path: "/purchase_units/@reference_id=='default'/amount",
        value: buildUpdatePaypalOrderInput(result.data.updateCheckoutShippingEstimate.checkout),
      },
    ]);
  }

  function onError() {
    const error = { message: I18n.t('discovery.checkout.paypal.error') };
    setErrors([error]);
  }

  const options = {
    currency,
    'client-id': PAYPAL_CLIENT_ID,
    intent: 'capture',
  };

  return (
    <div className="checkout-cart-footer__button checkout-cart-footer__button--paypal">
      <PayPalScriptProvider options={options}>
        <PayPalButtons
          fundingSource="paypal"
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onError}
          onShippingChange={onShippingChange}
          style={{
            shape: 'pill',
            height: 36,
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}

function buildUpdatePaypalOrderInput(checkout) {
  function buildPaypalLineItem(lineItemType) {
    return {
      value: checkout.lineItems.find(lineItem => lineItem.type === lineItemType).amount.amount,
      currency_code: checkout.lineItems.find(lineItem => lineItem.type === lineItemType).amount.currency,
    };
  }

  return {
    ...buildPaypalLineItem(CheckoutLineItemType.AMOUNT_OWED),
    breakdown: {
      item_total: buildPaypalLineItem(CheckoutLineItemType.AMOUNT_LISTING_SUBTOTAL),
      tax_total: buildPaypalLineItem(CheckoutLineItemType.AMOUNT_TAX),
      shipping: buildPaypalLineItem(CheckoutLineItemType.AMOUNT_SHIPPING),
    },
  };
}

const withStartPaypalCheckoutMutation = withGraphql<
ExternalProps,
CoreCheckoutStartPaypalCheckout.Mutation,
CoreCheckoutStartPaypalCheckout.Variables
>(
  gql`
  mutation Core_Checkout_StartPaypalCheckout(
    $input: Input_core_apimessages_StartPaypalCheckoutRequest
  ) {
    startPaypalCheckout(input: $input) {
      checkout {
        _id
        id
      }
      paypalOrderId
    }
  }`,
  {
    name: 'startPaypalCheckout',
  },
);

const withFinalizePaypalCheckoutPaymentMutation = withGraphql<
ExternalProps,
CoreCheckoutFinalizePaypalCheckoutPayment.Mutation,
CoreCheckoutFinalizePaypalCheckoutPayment.Variables
>(
  gql`
  mutation Core_Checkout_FinalizePaypalCheckoutPayment(
    $input: Input_core_apimessages_FinalizePaypalCheckoutPaymentRequest
  ) {
    finalizePaypalCheckoutPayment(input: $input) {
      checkout {
        _id
        id
      }
    }
  }`,
  {
    name: 'finalizePaypalCheckoutPayment',
  },
);

const withUpdateShippingEstimateMutation = withGraphql<
ExternalProps,
CoreCheckoutUpdatePaypalShippingEstimate.Mutation,
CoreCheckoutUpdatePaypalShippingEstimate.Variables
>(
  gql`
  mutation Core_Checkout_UpdatePaypalShippingEstimate(
    $input: Input_core_apimessages_CheckoutUpdateShippingEstimateRequest
  ) {
    updateCheckoutShippingEstimate(input: $input) {
      checkout {
        _id
        id
        lineItems {
          type
          amount {
            amount
            currency
          }
        }
      }
    }
  }`,
  {
    name: 'updatePaypalShippingEstimate',
  },
);

const withQuery = withGraphql<Props, CoreCheckoutPayPalPaymentInProgress.Query>(
  gql`
    query Core_Checkout_PayPalPaymentInProgress($id: String, $skip: Boolean!){
      checkout(input: { id: $id }) @skip(if: $skip) {
        _id
        action {
          _id
          ...CheckoutActionHookFields
        }
      }
    }
    ${CheckoutActionHookFragment}
  `,
  {
    options: () => {
      return {
        variables: {
          skip: true,
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      };
    },
  },
);

export default connect<ExternalProps>([
  withStartPaypalCheckoutMutation,
  withFinalizePaypalCheckoutPaymentMutation,
  withUpdateShippingEstimateMutation,
  withQuery,
])(CheckoutPayPalButton);
